import { FC, ReactNode, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './card.module.sass';

// ──────────────────────────────────────────────────────────────
// METRIC CARD
// ──────────────────────────────────────────────────────────────

export type TrendDirection = 'up' | 'down';
export type TrendSentiment = 'positive' | 'negative' | 'neutral';

export interface MetricCardProps {
  /** Label text displayed above the value */
  label: string;
  /** Main value to display */
  value: string | number;
  /** Subtitle text displayed below value (optional) */
  subtitle?: string;
  /** Descriptive definition of the metric, rendered as a muted multi-line block at the bottom */
  description?: string;
  /** Info tooltip — shows an (i) icon next to the label */
  tooltip?: string;
  /** Trend badge with direction and percentage */
  trend?: {
    direction: TrendDirection;
    value: string | number;
    /** Override sentiment (for metrics where up=bad like DQ rate) */
    sentiment?: TrendSentiment;
  };
  /** Inline sparkline data points — rendered as a mini AreaSpark below the value */
  sparkData?: number[];
  /** Opt-in "lit glass" hover: the pane catches light under the pointer, tilts a
   *  few degrees and clears its frost. */
  interactive?: boolean;
  /** Additional CSS class */
  className?: string;
}

/**
 * MetricCard - displays a label, value, optional trend badge, and optional subtitle
 * Used for displaying key metrics like balance, revenue, etc.
 *
 * @example
 * <MetricCard
 *   label="Total Balance"
 *   value="$128,470.66"
 *   trend={{ direction: 'up', value: '+12.5%' }}
 * />
 *
 * @example
 * <MetricCard
 *   label="Next Payment"
 *   value="$21,250"
 *   subtitle="15 Jul 2026"
 * />
 */
export const MetricCard: FC<MetricCardProps> = ({
  label,
  value,
  subtitle,
  description,
  tooltip,
  trend,
  sparkData,
  interactive,
  className,
}) => {
  const [showTip, setShowTip] = useState(false);
  // Icon SVG for trend indicator
  const TrendUpIcon = () => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  );

  const TrendDownIcon = () => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
      <polyline points="17 18 23 18 23 12" />
    </svg>
  );

  // Resolve sentiment: explicit > default (up=positive, down=negative)
  const trendSentiment = trend?.sentiment ?? (trend?.direction === 'up' ? 'positive' : trend?.direction === 'down' ? 'negative' : undefined);
  const trendClass = trendSentiment === 'negative' ? 'down' : trendSentiment === 'positive' ? 'up' : trend?.direction;

  const labelEl = (
    <span className={styles.metricCardLabel}>
      {label}
      {tooltip && (
        <span
          className={styles.metricCardInfo}
          onMouseEnter={() => setShowTip(true)}
          onMouseLeave={() => setShowTip(false)}
        >
          i
        </span>
      )}
    </span>
  );

  const header = trend ? (
    <div className={styles.metricCardHeader}>
      {labelEl}
      <span
        className={classNames(
          styles.metricCardTrend,
          trendClass && styles[trendClass]
        )}
      >
        {trend.direction === 'up' ? <TrendUpIcon /> : <TrendDownIcon />}
        {trend.value}
      </span>
    </div>
  ) : (
    labelEl
  );

  const faceBody = (
    <>
      {header}
      <span className={styles.metricCardValue}>{value}</span>
      {sparkData && sparkData.length > 1 && <MiniSpark data={sparkData} />}
      {subtitle && <span className={styles.metricCardSub}>{subtitle}</span>}
    </>
  );

  const frontContent = (
    <>
      {faceBody}
      {description && <p className={styles.metricCardDescription}>{description}</p>}
      {showTip && tooltip && (
        <div className={styles.metricCardTooltip}>{tooltip}</div>
      )}
    </>
  );

  const cardRef = useRef<HTMLDivElement>(null);

  // "Catch the light": the pane refracts under the pointer (glare follows, the
  // surface tilts a few degrees) and clears its frost on hover.
  if (interactive) {
    return (
      <div
        ref={cardRef}
        className={classNames(styles.metricCard, styles.metricCardLit, className, 'relative')}
        onMouseMove={(e) => {
          const el = cardRef.current;
          if (!el) return;
          const r = el.getBoundingClientRect();
          const px = (e.clientX - r.left) / r.width;
          const py = (e.clientY - r.top) / r.height;
          el.style.setProperty('--mx', `${(px * 100).toFixed(1)}%`);
          el.style.setProperty('--my', `${(py * 100).toFixed(1)}%`);
          el.style.setProperty('--rx', `${((0.5 - py) * 18).toFixed(2)}deg`);
          el.style.setProperty('--ry', `${((px - 0.5) * 18).toFixed(2)}deg`);
        }}
        onMouseLeave={() => {
          const el = cardRef.current;
          if (!el) return;
          el.style.setProperty('--rx', '0deg');
          el.style.setProperty('--ry', '0deg');
        }}
      >
        <span className={styles.litAurora} aria-hidden="true" />
        <span className={styles.litGlare} aria-hidden="true" />
        {frontContent}
      </div>
    );
  }

  return (
    <div className={classNames(styles.metricCard, className, 'relative')}>
      {frontContent}
    </div>
  );
};

MetricCard.displayName = 'MetricCard';

/** Tiny inline SVG sparkline for MetricCard — no external deps */
const MiniSpark: FC<{ data: number[] }> = ({ data }) => {
  const w = 120;
  const h = 28;
  const pad = 1;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = pad + (i / (data.length - 1)) * (w - pad * 2);
    const y = pad + (1 - (v - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  });
  const pathD = `M${points.join(' L')}`;
  const areaD = `${pathD} L${w - pad},${h} L${pad},${h} Z`;

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className={styles.metricCardSpark}>
      <path d={areaD} fill="var(--chart-fill, rgba(37,99,235,0.08))" />
      <path d={pathD} fill="none" stroke="var(--chart-1, #0F2341)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// ──────────────────────────────────────────────────────────────
// CHART CARD
// ──────────────────────────────────────────────────────────────

export interface ChartCardProps {
  /** Title text displayed in header */
  title: string;
  /** Chart content (SVG or custom content) */
  children: ReactNode;
  /** Optional action button text - if provided, button is shown */
  actionLabel?: string;
  /** Callback when action button is clicked */
  onAction?: () => void;
  /** Additional CSS class */
  className?: string;
}

/**
 * ChartCard - container for data visualizations like area charts, bar charts, donut charts
 * Includes header with title and optional action button
 *
 * @example
 * <ChartCard title="Monthly Income Trend" actionLabel="See All" onAction={handleViewAll}>
 *   <svg>...</svg>
 * </ChartCard>
 */
export const ChartCard: FC<ChartCardProps> = ({
  title,
  children,
  actionLabel,
  onAction,
  className,
}) => {
  return (
    <div className={classNames(styles.chartCard, className)}>
      <div className={styles.chartCardHeader}>
        <span className={styles.chartCardTitle}>{title}</span>
        {actionLabel && (
          <button
            className={styles.chartCardAction}
            onClick={onAction}
            type="button"
          >
            {actionLabel}
          </button>
        )}
      </div>

      <div className={styles.chartCardBody}>{children}</div>
    </div>
  );
};

ChartCard.displayName = 'ChartCard';

// ──────────────────────────────────────────────────────────────
// BASE CARD
// ──────────────────────────────────────────────────────────────

export interface CardProps {
  /** Card content */
  children: ReactNode;
  /** Additional CSS class */
  className?: string;
  /** Click handler */
  onClick?: () => void;
}

/**
 * Card - basic container with glass-effect styling
 * Use for general purpose content containers
 *
 * @example
 * <Card>
 *   <h3>Card Title</h3>
 *   <p>Card content here</p>
 * </Card>
 */
export const Card: FC<CardProps> = ({ children, className, onClick }) => {
  return <div className={classNames(styles.card, className)} onClick={onClick}>{children}</div>;
};

Card.displayName = 'Card';
